import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

// --- Components & Styles ---
import '../Styles/SettlePayment.css';
const API_URL = process.env.REACT_APP_API_URL;
const SettlePayment = () => {
    // --- State Management ---
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    
    // ✅ State for the pre-selected invoice from URL
    const [preselectedInvoice, setPreselectedInvoice] = useState(null);
    
    const [matches, setMatches] = useState([]);
    const [selectedMatchIndex, setSelectedMatchIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // --- Hooks ---
    const location = useLocation(); // ✅ Restored useLocation hook
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // Fetch customers on initial load
    useEffect(() => {
        if (!token) navigate("/login");
        axios.get(`${API_URL}/customers`, { headers })
            .then(res => setCustomers(res.data))
            .catch(err => console.error("Failed to fetch customers:", err));
    }, [headers, navigate, token]);

    // ✅ EFFECT RESTORED: Check for pre-selected invoice details in URL
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const customerCode = queryParams.get('customer');
        const docno = queryParams.get('docno');
        const finyear = queryParams.get('finyear');
        const amount = queryParams.get('amount');
        const docdate = queryParams.get('docdate');

        if (docno && finyear && amount && docdate && customerCode) {
            setSelectedCustomer(customerCode);
            setAmount(amount);
            setPreselectedInvoice({
                docno,
                finyear,
                due_amount: parseFloat(amount),
                docdate: docdate
            });
        }
    }, [location.search]);

    // --- Event Handlers ---
    const handleFindMatches = async () => {
        if (!selectedCustomer || !amount) {
            setError("Please select a customer and enter an amount.");
            return;
        }
        setLoading(true);
        setError('');
        setMatches([]);
        setSelectedMatchIndex(null);
        try {
            // ⭐ FIX: Send amount as a string to preserve precision
            const payload = { customer_code: selectedCustomer, amount: amount };
            const response = await axios.post(`${API_URL}/payments/find-matches`, payload, { headers });
            setMatches(response.data);
            if (response.data.length === 0) setError("No exact invoice combinations match this amount.");
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to find matches.");
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsPaid = async () => {
        // ✅ LOGIC UPDATED: Now handles both preselected and matched invoices
        setLoading(true);
        setError('');
        setSuccess('');

        let invoicesToPay = [];
        if (preselectedInvoice) {
            invoicesToPay = [preselectedInvoice];
        } else if (selectedMatchIndex !== null) {
            invoicesToPay = matches[selectedMatchIndex];
        } else {
            setError("Please select a matched set of invoices.");
            setLoading(false);
            return;
        }

        const payload = { invoices: invoicesToPay, payment_date: paymentDate };

        try {
            const response = await axios.post(`${API_URL}/payments/mark-as-paid`, payload, { headers });
            setSuccess(response.data.message + " You will be redirected shortly.");
            setTimeout(() => navigate("/manage-outstandings"), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to mark invoices as paid.");
        } finally {
            setLoading(false);
        }
    };

    // ✅ RENDER LOGIC RESTORED: Show a different UI for pre-selected invoices
    if (preselectedInvoice) {
        return (
                <div className="settle-payment-scroll-wrapper">
                <div className="settle-payment-container">
                    <div className="card">
                        <div className="card-header">
                            <h3>Confirm Payment for a Specific Invoice</h3>
                        </div>
                        <div className="card-body">
                            <div className="preselected-info">
                                You are settling Invoice <strong>#{preselectedInvoice.docno}</strong> for the amount of <strong>₹{parseFloat(preselectedInvoice.due_amount).toFixed(2)}</strong>.
                            </div>
                            <div className="form-group">
                                <label htmlFor="paymentDate">Payment Date</label>
                                <input id="paymentDate" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                            </div>
                        </div>
                        <div className="card-footer">
                             <button onClick={() => navigate(-1)} className="btn btn-outline">Cancel</button>
                            <button onClick={handleMarkAsPaid} disabled={loading} className="btn btn-success">
                                {loading ? 'Saving...' : 'Confirm & Mark as Paid'}
                            </button>
                        </div>
                    </div>
                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}
                </div>
            </div>
        );
    }

    // --- Default Render for general settlement ---
    return (
        <div className="settle-payment-scroll-wrapper">
            <div className="settle-payment-container">
                {/* Step 1 Card */}
                <div className="card">
                    <div className="card-header"><h3>Step 1: Enter Payment Details</h3></div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="customer">Customer</label>
                                <select id="customer" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                                    <option value="">Select a Customer</option>
                                    {customers.map(c => <option key={c.customercode} value={c.customercode}>{c.customername}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="amount">Amount Received</label>
                                <input id="amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 955.00" />
                            </div>
                        </div>
                    </div>
                    <div className="card-footer">
                        <button className="btn btn-primary" onClick={handleFindMatches} disabled={loading || !selectedCustomer || !amount}>
                            {loading ? 'Searching...' : 'Find Matching Invoices'}
                        </button>
                    </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                
                {/* Step 2 & 3 Cards (same as before) */}
                {matches.length > 0 && (
                    <>
                        <div className="card">
                            <div className="card-header">
                                <h3>Step 2: Select Invoice Combination</h3>
                                <p>Select the combination of invoices you want to mark as paid.</p>
                            </div>
                            <div className="card-body">
                                {matches.map((match, index) => (
                                    <div key={index} className="match-group">
                                        <label className="radio-label">
                                            <input type="radio" name="matchSelection" checked={selectedMatchIndex === index} onChange={() => setSelectedMatchIndex(index)} />
                                            <span>Match Combination {index + 1}</span>
                                        </label>
                                        <ul className="invoice-list">
                                            {match.map(inv => (
                                                <li key={inv.docno}>
                                                    Invoice <strong>#{inv.docno}</strong> <span>({new Date(inv.docdate).toLocaleDateString("en-GB")})</span> - <strong>₹{parseFloat(inv.due_amount).toFixed(2)}</strong>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-header"><h3>Step 3: Confirm Payment</h3></div>
                            <div className="card-body">
                                <div className="form-group">
                                    <label htmlFor="paymentDate">Payment Date</label>
                                    <input id="paymentDate" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                                </div>
                            </div>
                            <div className="card-footer">
                                <button onClick={handleMarkAsPaid} className="btn btn-success" disabled={loading || selectedMatchIndex === null}>
                                    {loading ? 'Saving...' : 'Confirm & Mark as Paid'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
                
                {success && <div className="alert alert-success">{success}</div>}
            </div>
        </div>
    );
};

export default SettlePayment;
